import React, { Fragment, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { contactNoError, emailError } from './contactValidation';

export const defaultBusinessContact = {
  customer_business_contact_id: '',
  designation: '',
  name: '',
  contact_no: '',
  email: '',
  set_as_purchase_info: 0,
  set_as_person_to_receive: 0,
  set_as_ecr_recipient: 0,
  set_as_soa_recipient: 0,
};

const FLAGS = [
  { key: 'set_as_purchase_info', label: 'Set as Purchase Info' },
  { key: 'set_as_person_to_receive', label: 'Set as Person to Receive' },
  { key: 'set_as_ecr_recipient', label: 'Set as E-CR Recipient' },
  { key: 'set_as_soa_recipient', label: 'Set as SOA Recipient' },
];

/**
 * Tags a customer must always keep at least one holder of — same restriction
 * that blocks create/save in CreateCustomer / ViewCustomer.
 */
const REQUIRED_TAGS = [
  { key: 'set_as_soa_recipient', label: 'SOA Recipient' },
  { key: 'set_as_ecr_recipient', label: 'E-CR Recipient' },
];

/**
 * Required tags held by the row at `selectedIndex` that no other contact
 * carries. Non-empty means removing the row — or untagging it — would leave
 * the customer with no holder, so the action has to be blocked.
 */
const lastHeldTags = (rows, selectedIndex) =>
  REQUIRED_TAGS.filter(
    (tag) =>
      !!rows[selectedIndex][tag.key] &&
      !rows.some((row, index) => index !== selectedIndex && !!row[tag.key]),
  );

/**
 * Editable Business Contacts list (Account Information).
 * Mirrors the ViewCustomerShippingAddresses pattern: keeps its own array
 * state, seeded from `data`, and pushes changes to the parent via `onChange`.
 * Each contact holds one contact no. and one email plus the 4 recipient flags.
 */
const BusinessContactsList = ({ data, onChange, disabled = false }) => {
  const [contacts, setContacts] = useState([defaultBusinessContact]);

  useEffect(() => {
    if (Array.isArray(data) && data.length) {
      setContacts(data);
    }
  }, [data]);

  useEffect(() => {
    const timeOutId = setTimeout(() => onChange && onChange(contacts), 400);
    return () => clearTimeout(timeOutId);
  }, [contacts]);

  const handleAddContact = () => {
    setContacts([...contacts, { ...defaultBusinessContact }]);
  };

  const handleDeleteContact = (selectedIndex) => {
    // ESCM-13070: block the delete while this contact is the only holder of a
    // required tag — the customer must always be left with a tagged recipient.
    const blocking = lastHeldTags(contacts, selectedIndex);
    if (blocking.length) {
      Swal.fire({
        icon: 'error',
        title: 'Business Contacts',
        text: `This contact is the only ${blocking
          .map((tag) => tag.label)
          .join(
            ' and ',
          )} of this customer. Please tag another contact first before deleting this one.`,
      });
      return;
    }
    setContacts(contacts.filter((row, index) => index !== selectedIndex));
  };

  /**
   * ESCM-13070: the CONTACT NO. box used to swallow every non-digit keystroke,
   * so "N/A" could not be typed at all even though not every contact has a
   * number to record. Nothing is filtered out on the way in any more — what a
   * box may hold is reported inline by contactNoError/emailError and blocked on
   * save by validateAccountingContacts, which is the same rule set the BE runs.
   */
  const handleChangeField = (selectedIndex, name, value) => {
    setContacts(
      contacts.map((row, index) =>
        index === selectedIndex ? { ...row, [name]: value } : row,
      ),
    );
  };

  const handleToggleFlag = (selectedIndex, key, checked) => {
    // ESCM-13070: same restriction on untagging — an existing SOA / E-CR tag
    // can only be removed once another contact carries it.
    if (!checked) {
      const blocking = lastHeldTags(contacts, selectedIndex).find(
        (tag) => tag.key === key,
      );
      if (blocking) {
        Swal.fire({
          icon: 'error',
          title: 'Business Contacts',
          text: `Each customer must have at least one ${blocking.label}. Please tag another contact as ${blocking.label} first before removing it from this one.`,
        });
        return;
      }
    }
    setContacts(
      contacts.map((row, index) =>
        index === selectedIndex ? { ...row, [key]: checked ? 1 : 0 } : row,
      ),
    );
  };

  const hasSOARecipient = contacts.some((c) => c.set_as_soa_recipient);
  const hasECRRecipient = contacts.some((c) => c.set_as_ecr_recipient);

  const inputStyle = { minWidth: '150px' };

  return (
    <Fragment>
      <fieldset className="details-field-set mb-4 mt-0">
        <legend>Account Information</legend>

        {(!hasSOARecipient || !hasECRRecipient) && (
          <div className="alert alert-warning py-2 px-3 mb-2" role="alert">
            Each customer must have at least one <b>SOA Recipient</b> and one{' '}
            <b>E-CR Recipient</b>.
            {!hasSOARecipient && ' No SOA Recipient is tagged.'}
            {!hasECRRecipient && ' No E-CR Recipient is tagged.'}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle mb-2">
            <thead>
              <tr className="text-center">
                <th style={{ minWidth: '150px' }}>Designation</th>
                <th style={{ minWidth: '160px' }}>Name of Contact</th>
                <th style={{ minWidth: '140px' }}>Contact No.</th>
                <th style={{ minWidth: '200px' }}>Email Address</th>
                {FLAGS.map((f) => (
                  <th key={f.key} style={{ width: '90px' }}>
                    {f.label}
                  </th>
                ))}
                <th style={{ width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((row, index) => {
                const contactNoInvalid = contactNoError(row.contact_no);
                const emailInvalid = emailError(row);
                return (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={inputStyle}
                        value={row.designation || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          handleChangeField(
                            index,
                            'designation',
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={inputStyle}
                        value={row.name || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          handleChangeField(index, 'name', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className={
                          'form-control form-control-sm' +
                          (contactNoInvalid ? ' is-invalid' : '')
                        }
                        style={inputStyle}
                        placeholder="N/A if none"
                        value={row.contact_no || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          handleChangeField(index, 'contact_no', e.target.value)
                        }
                      />
                      {contactNoInvalid && (
                        <small style={{ color: 'red' }}>
                          {contactNoInvalid}
                        </small>
                      )}
                    </td>
                    <td>
                      <input
                        type="email"
                        className={
                          'form-control form-control-sm' +
                          (emailInvalid ? ' is-invalid' : '')
                        }
                        style={inputStyle}
                        value={row.email || ''}
                        disabled={disabled}
                        onChange={(e) =>
                          handleChangeField(index, 'email', e.target.value)
                        }
                      />
                      {emailInvalid && (
                        <small style={{ color: 'red' }}>{emailInvalid}</small>
                      )}
                    </td>
                    {FLAGS.map((f) => (
                      <td key={f.key} className="text-center">
                        <input
                          type="checkbox"
                          style={{ width: '18px', height: '18px' }}
                          checked={!!row[f.key]}
                          disabled={disabled}
                          onChange={(e) =>
                            handleToggleFlag(index, f.key, e.target.checked)
                          }
                        />
                      </td>
                    ))}
                    <td className="text-center">
                      {!disabled && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          title="Delete contact"
                          onClick={() => handleDeleteContact(index)}
                        >
                          &times;
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {contacts.length === 0 && (
                <tr>
                  <td
                    colSpan={5 + FLAGS.length}
                    className="text-center text-muted"
                  >
                    No contacts yet. Click “Add New Contact”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!disabled && (
          <button
            type="button"
            className="btn btn-info btn-sm"
            onClick={handleAddContact}
          >
            + Add New Contact
          </button>
        )}
      </fieldset>
    </Fragment>
  );
};

export default BusinessContactsList;
